import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFlooScopeOptions } from '@/hooks/useFlooScopeOptions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Building2, Map as MapIcon, Layers, Store, ArrowLeft, Network, Info,
} from 'lucide-react';
import { format } from 'date-fns';
import { clock } from '@/utils/clock';
import { cn } from '@/lib/utils';

// Floo Network Phase 7 — Region Dashboard.
//
// Structural + lightweight-operational view of a single region. Surfaces:
//   • region metadata (name, parent org, archived state)
//   • district breakdown with drill-down to district dashboards
//   • per-shop metrics aggregated from `get_org_dashboard_metrics`
//
// Authorization is two-layered:
//   1. Route-level: ProtectedRoute admits OWNER / ORG_ADMIN / MANAGER /
//      REGION_MANAGER / DISTRICT_MANAGER.
//   2. Page-level: we verify the target region appears in the user's
//      `useFlooScopeOptions.options` set. This is the canonical access
//      gate Phase 6 already validates against the user's membership
//      boundary, so we don't have to re-derive it here.
//
// Metric scope intentionally narrow for Phase 7: only counts and the
// per-shop active/completed/waiter snapshot. Deferred to Phase 7B / 8:
// estimated_revenue, capacity utilization, OBLI scoring, AI-driven
// recommendations.

type RegionRow = {
  id: string;
  name: string;
  organization_id: string;
  archived_at: string | null;
};

type DistrictRow = {
  id: string;
  name: string;
  organization_id: string;
  region_id: string | null;
  archived_at: string | null;
};

type PerShop = {
  shop_id: string;
  shop_name: string;
  address: string | null;
  active_tickets: number;
  completed_in_range: number;
  booked_minutes: number;
  waiter_count: number;
  total_techs: number;
  total_capacity: number;
};

type DashboardMetrics = {
  total_shops: number;
  active_tickets: number;
  completed_in_range: number;
  vehicles_in_shop: number;
  estimated_revenue: number | null;
  total_booked_minutes: number;
  total_capacity_minutes: number;
  per_shop: PerShop[];
};

const RegionDashboard = () => {
  const navigate = useNavigate();
  const { regionId } = useParams<{ regionId: string }>();
  const { user, userShops, isShopDataLoading } = useAuth();
  const {
    boundary,
    isLoading: scopeLoading,
    error: scopeError,
  } = useFlooScopeOptions();

  // ── Access check ─────────────────────────────────────────────────────
  // Stabilization fix: boundary-based, not options-based.
  //
  // `useFlooScopeOptions.options` filters out archived regions, so an
  // options-based access check would reject every direct URL to an
  // archived region — including for an org owner who has every right
  // to view the archived state. The boundary is the user's permission
  // ceiling and does not change with archived state, so we anchor the
  // gate there and let the page render its archived banner.
  //
  // We need the region's organization_id before we can finalize the
  // check, so this is computed after `region` resolves below.

  // ── Region + districts metadata ──────────────────────────────────────
  const regionQuery = useQuery({
    queryKey: ['floo-region-dashboard', 'region', regionId],
    enabled: !!regionId,
    queryFn: async (): Promise<RegionRow | null> => {
      const { data, error } = await supabase
        .from('regions')
        .select('id,name,organization_id,archived_at')
        .eq('id', regionId!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as RegionRow | null;
    },
  });

  const orgQuery = useQuery({
    queryKey: ['floo-region-dashboard', 'org', regionQuery.data?.organization_id ?? null],
    enabled: !!regionQuery.data?.organization_id,
    queryFn: async (): Promise<{ id: string; name: string } | null> => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id,name')
        .eq('id', regionQuery.data!.organization_id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as { id: string; name: string } | null;
    },
  });

  const districtsQuery = useQuery({
    queryKey: ['floo-region-dashboard', 'districts', regionId],
    enabled: !!regionId,
    queryFn: async (): Promise<DistrictRow[]> => {
      const { data, error } = await supabase
        .from('districts')
        .select('id,name,organization_id,region_id,archived_at')
        .eq('region_id', regionId!)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as DistrictRow[];
    },
  });

  // ── Per-shop metrics (filtered client-side to region) ────────────────
  // The org-wide RPC returns metrics for every accessible shop; we
  // filter to the region's shops based on AuthContext.userShops, which
  // is already RLS-resolved.
  const metricsQuery = useQuery({
    queryKey: ['floo-region-dashboard', 'metrics', user?.id, todayString()],
    enabled: !!user?.id,
    queryFn: async (): Promise<DashboardMetrics | null> => {
      const today = todayString();
      const { data, error } = await supabase.rpc('get_org_dashboard_metrics', {
        _user_id: user!.id,
        _start_date: today,
        _end_date: today,
      });
      if (error) throw error;
      return (data as unknown as DashboardMetrics) ?? null;
    },
  });

  // ── Derived data ─────────────────────────────────────────────────────
  const region = regionQuery.data;

  // Boundary-based access check, finalized once `region` resolves.
  // OWNER / ORG_ADMIN / management-role at organization scope reach any
  // region inside their org. REGION_MANAGER assigned to this region
  // also passes. Anything else (DISTRICT_MANAGER, shop-scoped MANAGER,
  // operational roles) is denied.
  const hasAccess = useMemo(() => {
    if (!boundary || !region) return false;
    if (boundary.type === 'organization' && boundary.id === region.organization_id) return true;
    if (boundary.type === 'region' && boundary.id === region.id) return true;
    return false;
  }, [boundary, region]);
  const org = orgQuery.data;
  const districts = districtsQuery.data ?? [];
  const activeDistricts = districts.filter((d) => !d.archived_at);

  const regionShops = useMemo(
    () => userShops.filter((s) => s.region_id === regionId),
    [userShops, regionId],
  );
  const activeShops = regionShops.filter((s) => s.shop_status === 'active');

  const perShopByShop = useMemo(() => {
    const m = new Map<string, PerShop>();
    for (const row of metricsQuery.data?.per_shop ?? []) {
      m.set(row.shop_id, row);
    }
    return m;
  }, [metricsQuery.data]);

  const regionRollup = useMemo(() => {
    let activeTickets = 0;
    let completed = 0;
    let waiters = 0;
    let techs = 0;
    let capacity = 0;
    let bookedMinutes = 0;
    for (const s of regionShops) {
      const row = perShopByShop.get(s.id);
      if (!row) continue;
      activeTickets += row.active_tickets;
      completed += row.completed_in_range;
      waiters += row.waiter_count;
      techs += row.total_techs;
      capacity += row.total_capacity;
      bookedMinutes += row.booked_minutes;
    }
    return { activeTickets, completed, waiters, techs, capacity, bookedMinutes };
  }, [regionShops, perShopByShop]);

  const loading =
    isShopDataLoading ||
    scopeLoading ||
    regionQuery.isLoading ||
    orgQuery.isLoading ||
    districtsQuery.isLoading ||
    metricsQuery.isLoading;

  const lookupError =
    (regionQuery.error ?? orgQuery.error ?? districtsQuery.error ?? scopeError ?? null) as Error | null;

  // ── Render guards ────────────────────────────────────────────────────
  // Order matters: we show "not found" when the row itself didn't load
  // (could be deleted OR RLS-denied — both render the same so we don't
  // leak the existence of rows the user can't see). Then we show
  // "access denied" only when the row WAS returned but the user's
  // boundary doesn't grant access (a deliberate scope mismatch).
  if (!regionId) {
    return <NotFoundCard label="Region" />;
  }

  if (!loading && !region) {
    return <NotFoundCard label="Region" />;
  }

  if (!loading && region && !hasAccess) {
    return (
      <DashboardShell>
        <Alert variant="destructive">
          <AlertTitle>Access denied</AlertTitle>
          <AlertDescription>
            You do not have access to this region. Contact an owner or
            organization admin if you believe this is unexpected.
          </AlertDescription>
        </Alert>
      </DashboardShell>
    );
  }

  if (lookupError) {
    return (
      <DashboardShell>
        <Alert variant="destructive">
          <AlertTitle>Couldn’t load region data</AlertTitle>
          <AlertDescription>
            A required table returned an error. No changes have been made.
          </AlertDescription>
        </Alert>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      {/* Header — Stage 2 back navigation. Primary is the OptiNetwork
          hub (consistent with other OptiNetwork surfaces); secondary
          keeps the legacy "OptiNetwork Map" affordance with its existing
          `floo-back-to-map` testid so the p7 §E doctrinal scan that
          asserts the map navigate target still resolves. */}
      <header className="flex items-center gap-2 mb-4 flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/optinetwork')}
          className="gap-1 text-xs"
          data-testid="region-dashboard-back-to-opti-network"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to OptiNetwork
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/optinetwork/map')}
          className="gap-1 text-xs"
          data-testid="floo-back-to-map"
        >
          OptiNetwork Map
        </Button>
      </header>

      <div className="flex items-start gap-3 mb-2">
        <div className="rounded-md bg-primary/10 p-2 text-primary">
          <MapIcon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{region?.name ?? 'Region'}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1.5 flex-wrap">
            <Building2 className="h-3 w-3" />
            <span>{org?.name ?? 'Organization'}</span>
            {region?.archived_at && (
              <Badge variant="outline" className="text-[10px]">Archived</Badge>
            )}
          </p>
        </div>
      </div>

      {region?.archived_at && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertTitle>Region is archived</AlertTitle>
          <AlertDescription>
            This region has been archived. Its districts and shops are still
            addressable, but new members and child entities cannot be assigned
            to it. Unarchive from the OptiNetwork Map to reactivate.
          </AlertDescription>
        </Alert>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard
          label="Districts"
          value={activeDistricts.length}
          icon={<Layers className="h-4 w-4" />}
        />
        <KpiCard
          label="Active shops"
          value={activeShops.length}
          icon={<Store className="h-4 w-4" />}
        />
        <KpiCard
          label="Active tickets today"
          value={regionRollup.activeTickets}
        />
        <KpiCard
          label="Completed today"
          value={regionRollup.completed}
        />
      </div>

      {/* Districts table */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="h-4 w-4 text-muted-foreground" />
            Districts in this region
            <Badge variant="outline" className="text-[10px]">
              {activeDistricts.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeDistricts.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No active districts in this region yet. Use the OptiNetwork
              Map to create districts and assign shops.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>District</TableHead>
                  <TableHead className="text-right">Shops</TableHead>
                  <TableHead className="text-right">Active today</TableHead>
                  <TableHead className="text-right">Completed today</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeDistricts.map((d) => {
                  const dShops = regionShops.filter((s) => s.district_id === d.id);
                  let dActive = 0;
                  let dDone = 0;
                  for (const s of dShops) {
                    const r = perShopByShop.get(s.id);
                    if (!r) continue;
                    dActive += r.active_tickets;
                    dDone += r.completed_in_range;
                  }
                  return (
                    <TableRow key={d.id} data-testid={`floo-region-district-row-${d.id}`}>
                      <TableCell className="font-medium">{d.name}</TableCell>
                      <TableCell className="text-right">{dShops.length}</TableCell>
                      <TableCell className="text-right">{dActive}</TableCell>
                      <TableCell className="text-right">{dDone}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/org/districts/${d.id}`)}
                          data-testid={`floo-region-open-district-${d.id}`}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Shops table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Store className="h-4 w-4 text-muted-foreground" />
            Shops in this region
            <Badge variant="outline" className="text-[10px]">
              {regionShops.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {regionShops.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No shops in this region yet. Attach shops to districts via the
              OptiNetwork Map.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shop</TableHead>
                  <TableHead>District</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Active</TableHead>
                  <TableHead className="text-right">Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {regionShops.map((s) => {
                  const r = perShopByShop.get(s.id);
                  const district = activeDistricts.find((d) => d.id === s.district_id);
                  const inactive = s.shop_status !== 'active';
                  return (
                    <TableRow key={s.id} data-testid={`floo-region-shop-row-${s.id}`}>
                      <TableCell
                        className={cn(
                          'font-medium',
                          inactive && 'line-through text-muted-foreground',
                        )}
                      >
                        {s.name}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {district?.name ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={inactive ? 'outline' : 'secondary'} className="text-[10px]">
                          {s.shop_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{r?.active_tickets ?? 0}</TableCell>
                      <TableCell className="text-right">{r?.completed_in_range ?? 0}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardShell>
  );
};

function DashboardShell({ children }: { children: React.ReactNode }) {
  return <div className="container mx-auto py-6 px-4 max-w-5xl">{children}</div>;
}

function KpiCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="py-3 px-4">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          {icon}
          {label}
        </p>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}

function NotFoundCard({ label }: { label: string }) {
  return (
    <DashboardShell>
      <Card>
        <CardContent className="py-10 text-center space-y-2">
          <Network className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="font-medium">{label} not found</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            The {label.toLowerCase()} you’re trying to open doesn’t exist or
            isn’t visible to your account. It may have been archived or
            removed.
          </p>
        </CardContent>
      </Card>
    </DashboardShell>
  );
}

function todayString(): string {
  return format(clock.now(), 'yyyy-MM-dd');
}

export default RegionDashboard;
